import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearMoneda, type CierreMensual, type PlanInfo } from '@cmms/shared';
import { api, ErrorApi } from '../../servicios/api.js';
import { ErroresFormulario } from '../../componentes/piezas.js';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { Aviso, Cargando, Etiqueta, Vacio } from '../../componentes/piezas.js';

/** Mes anterior en formato AAAA-MM: es el que normalmente se cierra. */
function periodoPorOmision(): string {
  const hoy = new Date();
  const mes = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  return `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, '0')}`;
}

export function ListaPlanes(): JSX.Element {
  const { puede } = useSesionOperador();
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [periodo, setPeriodo] = useState(periodoPorOmision());

  const { data: planes, isLoading } = useQuery({
    queryKey: ['plataforma', 'planes'],
    queryFn: () => api.get<PlanInfo[]>('/plataforma/planes'),
  });

  const guardar = useMutation({
    mutationFn: ({ codigo, datos }: { codigo: string | null; datos: unknown }) =>
      codigo === null
        ? api.post<PlanInfo>('/plataforma/planes', datos)
        : api.put<PlanInfo>(`/plataforma/planes/${codigo}`, datos),
    onSuccess: async () => {
      setCreando(false);
      await cliente.invalidateQueries({ queryKey: ['plataforma'] });
    },
  });

  const cerrar = useMutation({
    mutationFn: () => api.post<CierreMensual[]>('/plataforma/cierres', { periodo }),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['plataforma'] }),
  });

  if (isLoading) return <Cargando />;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Planes</h1>
          <p>Precio por asiento y mes, en soles. Cambiarlo afecta la facturación del período siguiente.</p>
        </div>
        {puede('plan:escribir') ? (
          <button type="button" className="boton" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nuevo plan'}
          </button>
        ) : null}
      </div>

      {guardar.isError ? <ErroresFormulario error={guardar.error} /> : null}

      {creando ? (
        <FormularioPlan
          enviando={guardar.isPending}
          onEnviar={(datos) => guardar.mutate({ codigo: null, datos })}
        />
      ) : null}

      {planes && planes.length > 0 ? (
        <section className="panel" style={{ marginBottom: 16 }}>
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Precio / asiento</th>
                  <th>Incluidos</th>
                  <th>Máximo</th>
                  <th>Suscritos</th>
                  <th>Estado</th>
                  {puede('plan:escribir') ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {planes.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                        {p.codigo}
                        {p.descripcion ? ` · ${p.descripcion}` : ''}
                      </div>
                    </td>
                    <td>{formatearMoneda(p.precioAsientoMensualPen)}</td>
                    <td className="numero-registro">{p.asientosIncluidos}</td>
                    <td className="numero-registro">{p.maxAsientos ?? 'Sin tope'}</td>
                    <td className="numero-registro">{p.tenantsSuscritos}</td>
                    <td>
                      {p.activo ? (
                        <Etiqueta tono="verde">Disponible</Etiqueta>
                      ) : (
                        <Etiqueta tono="gris">Retirado</Etiqueta>
                      )}
                    </td>
                    {puede('plan:escribir') ? (
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="boton secundario pequeno"
                          disabled={guardar.isPending}
                          onClick={() =>
                            guardar.mutate({ codigo: p.codigo, datos: { activo: !p.activo } })
                          }
                        >
                          {p.activo ? 'Retirar' : 'Reactivar'}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <Vacio titulo="Sin planes" detalle="Cree el primero para poder dar de alta organizaciones." />
      )}

      {/* El cierre congela lo facturable del mes. Vive junto a los planes porque
          es el precio de estos el que se sella al ejecutarlo. */}
      {puede('facturacion:cerrar') ? (
        <section className="panel">
          <div className="panel-cabecera">
            <h2>Cierre mensual</h2>
          </div>
          <div className="panel-cuerpo">
            <p style={{ fontSize: 13, color: 'var(--texto-medio)', marginTop: 0 }}>
              Congela asientos contratados, pico de uso y precio de cada organización vigente. Un
              período ya cerrado no se reescribe: eso es lo que lo vuelve evidencia de facturación.
            </p>

            {cerrar.isError ? <ErroresFormulario error={cerrar.error} /> : null}
            {cerrar.isSuccess ? (
              <Aviso tono="exito">
                {cerrar.data.length} organización(es) cerradas por{' '}
                {formatearMoneda(
                  cerrar.data.reduce((total, c) => total + Number(c.importePen), 0).toFixed(2),
                )}
                .
              </Aviso>
            ) : null}

            <form
              style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
              onSubmit={(e) => {
                e.preventDefault();
                cerrar.mutate();
              }}
            >
              <div className="campo" style={{ marginBottom: 0 }}>
                <label htmlFor="periodo">Período</label>
                <input
                  id="periodo"
                  type="month"
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="boton" disabled={cerrar.isPending}>
                {cerrar.isPending ? 'Cerrando…' : 'Cerrar período'}
              </button>
            </form>
          </div>
        </section>
      ) : null}
    </>
  );
}

function FormularioPlan({
  enviando,
  onEnviar,
}: {
  enviando: boolean;
  onEnviar: (datos: unknown) => void;
}): JSX.Element {
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [precio, setPrecio] = useState(49);
  const [incluidos, setIncluidos] = useState(1);
  const [maximo, setMaximo] = useState('');

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-cabecera">
        <h2>Nuevo plan</h2>
      </div>
      <form
        className="panel-cuerpo"
        onSubmit={(e) => {
          e.preventDefault();
          onEnviar({
            codigo,
            nombre,
            descripcion: descripcion || undefined,
            precioAsientoMensualPen: precio,
            asientosIncluidos: incluidos,
            maxAsientos: maximo === '' ? null : Number(maximo),
            activo: true,
          });
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div className="campo">
            <label htmlFor="codigo-plan">Código</label>
            <input
              id="codigo-plan"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toLowerCase())}
              pattern="[a-z0-9][a-z0-9-]{1,49}"
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="nombre-plan">Nombre</label>
            <input id="nombre-plan" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="precio-plan">Precio por asiento (PEN)</label>
            <input
              id="precio-plan"
              type="number"
              min={0}
              step="0.01"
              value={precio}
              onChange={(e) => setPrecio(Number(e.target.value))}
              required
            />
          </div>
          <div className="campo">
            <label htmlFor="incluidos-plan">Asientos incluidos</label>
            <input
              id="incluidos-plan"
              type="number"
              min={0}
              value={incluidos}
              onChange={(e) => setIncluidos(Number(e.target.value))}
            />
          </div>
          <div className="campo">
            <label htmlFor="maximo-plan">Máximo de asientos</label>
            <input
              id="maximo-plan"
              type="number"
              min={1}
              value={maximo}
              onChange={(e) => setMaximo(e.target.value)}
              placeholder="Sin tope"
            />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="descripcion-plan">Descripción</label>
          <input
            id="descripcion-plan"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            maxLength={1000}
          />
        </div>

        <button type="submit" className="boton" disabled={enviando}>
          {enviando ? 'Creando…' : 'Crear plan'}
        </button>
      </form>
    </section>
  );
}
